import { useNavigate } from "react-router";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "@/lib/cn";
import { useMe } from "@/api/useMe";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/Button";

/* ──────────────────────────────────────────────────────────────────────────
 * +CURA Studio top bar
 *
 * A slim, transparent strip pinned above the page content holding a single
 * Microsoft/Azure-style account control on the right: a pill button showing
 * the signed-in user's name + role with their avatar and a chevron. It opens
 * a Radix Popover with name, email, role and a "Sign out" action.
 *
 * The bar itself carries no chrome (no border, no background) so it doesn't
 * reintroduce the heavy top strip the user disliked — it simply reserves a
 * row so the control never overlaps a page header. On narrow widths the
 * name/role text collapses, leaving just the avatar + chevron.
 * ──────────────────────────────────────────────────────────────────────── */

function roleLabel(r: string) {
  const labels: Record<string, string> = {
    STUDIO_ADMIN: "Studio admin",
    CLIENT_ADMIN: "Client admin",
    PRACTICE_MANAGER: "Practice manager",
    APPROVER: "Approver",
    DESIGNER: "Designer",
    VIEWER: "Viewer",
  };
  return labels[r] ?? r;
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function TopBar() {
  const { data: me } = useMe();
  const { user: firebaseUser, signOut } = useAuth();
  const navigate = useNavigate();

  const displayName =
    me?.user?.name ?? firebaseUser?.displayName ?? firebaseUser?.email ?? "Signed in";
  const displayEmail = me?.user?.email || firebaseUser?.email || null;
  const displayRole = me?.user?.role
    ? roleLabel(me.user.role)
    : firebaseUser
      ? "Signed in"
      : "Demo persona";
  const initials =
    (displayName || "")
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?";

  async function handleSignOut() {
    await signOut();
    // Drop any dev persona too so we always land on the sign-in page.
    window.localStorage.removeItem("cura-studio:dev-user");
    navigate("/auth/signin", { replace: true });
  }

  // Avatar: a soft brand-tint disc with brand-primary initials and a hairline
  // ring — intentional and legible, never dark text on saturated blue.
  const avatar = (
    <span
      className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-[12px] font-semibold ring-1 ring-inset ring-[var(--brand-primary)]/25"
      style={{
        background: "var(--brand-primary-tint, color-mix(in srgb, var(--brand-primary) 12%, white))",
        color: "var(--brand-primary)",
      }}
      aria-hidden
    >
      {initials}
    </span>
  );

  return (
    <div
      data-chrome
      className="flex h-12 flex-shrink-0 items-center justify-end px-4"
    >
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(
              "group flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 sm:pr-3",
              "border border-transparent outline-none transition-colors",
              "hover:border-[var(--border-1)] hover:bg-white",
              "data-[state=open]:border-[var(--border-1)] data-[state=open]:bg-white",
              "focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]",
            )}
            aria-label="Account menu"
          >
            {avatar}
            {/* Name + role stack — hidden on narrow widths. */}
            <span className="hidden min-w-0 flex-col items-start leading-tight sm:flex">
              <span className="max-w-[14rem] truncate text-[13px] font-semibold text-fg-1">
                {displayName}
              </span>
              <span className="max-w-[14rem] truncate text-[11px] text-fg-mute">
                {displayRole}
              </span>
            </span>
            <ChevronDownIcon className="flex-shrink-0 text-fg-3 transition-transform group-data-[state=open]:rotate-180" />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            side="bottom"
            sideOffset={8}
            className="z-50 w-64 overflow-hidden rounded-lg border border-[var(--border-1)] bg-white shadow-lg"
          >
            <div className="flex items-center gap-3 border-b border-[var(--border-1)] px-4 py-3.5">
              <span
                className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full text-[14px] font-semibold ring-1 ring-inset ring-[var(--brand-primary)]/25"
                style={{
                  background:
                    "var(--brand-primary-tint, color-mix(in srgb, var(--brand-primary) 12%, white))",
                  color: "var(--brand-primary)",
                }}
                aria-hidden
              >
                {initials}
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-[13px] font-semibold text-fg-1">
                  {displayName}
                </div>
                {displayEmail && (
                  <div className="truncate text-[11px] text-fg-3">{displayEmail}</div>
                )}
                <div className="mt-0.5 truncate text-[11px] font-medium text-fg-mute">
                  {displayRole}
                </div>
              </div>
            </div>
            <div className="p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="w-full justify-start"
              >
                Sign out
              </Button>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
